/**
 * Styled-components for the FlightPlanner feature.
 * Kept separate from logic to make FlightPlanner.jsx easier to navigate.
 */

import styled from 'styled-components';
import { Badge } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router';

// ─── Layout ──────────────────────────────────────────────────────────────────

export const PageContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: calc(100vh - 70px);
	background-color: ${props => props.theme.colors.background};
`;

export const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0.75rem 1rem;
	background: ${props => props.theme.colors.glass};
	backdrop-filter: blur(20px);
	border-bottom: 1px solid ${props => props.theme.colors.glassBorder};
`;

export const HeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 1rem;
`;

export const MainContent = styled.div`
	display: flex;
	flex: 1;
	overflow: hidden;
`;

export const LeftSidebar = styled.div`
	width: 280px;
	background: ${props => props.theme.colors.glass};
	backdrop-filter: blur(20px);
	border-right: 1px solid ${props => props.theme.colors.glassBorder};
	padding: 1rem;
	overflow-y: auto;
	overflow-x: hidden;
`;

export const MapContainer = styled.div`
	flex: 1;
	position: relative;
	background-color: #0a0e14;
`;

export const RightSidebar = styled.div`
	width: 420px;
	background: ${props => props.theme.colors.glass};
	backdrop-filter: blur(20px);
	border-left: 1px solid ${props => props.theme.colors.glassBorder};
	padding: 1rem;
	overflow-y: auto;
`;

// ─── Header elements ─────────────────────────────────────────────────────────

export const BackLink = styled(Link)`
	color: ${props => props.theme.colors.textAccent};
	text-decoration: none;
	font-size: 0.9rem;
	transition: color 0.2s ease;

	&:hover {
		color: ${props => props.theme.colors.green};
	}
`;

export const Title = styled.h1`
	color: ${props => props.theme.colors.textColor};
	font-size: 1.25rem;
	margin: 0;
	font-weight: 600;
`;

export const StepBadges = styled.div`
	display: flex;
	gap: 0.5rem;
`;

export const StepBadge = styled(Badge)`
	padding: 0.5rem 0.75rem;
	font-weight: 500;
	font-size: 0.8rem;
	border-radius: 20px;
	background: ${props =>
		props.$active
			? props.theme.colors.green
			: props.$completed
				? 'rgba(52, 211, 153, 0.2)'
				: 'rgba(255, 255, 255, 0.05)'} !important;
	color: ${props =>
		props.$active
			? '#fff'
			: props.$completed
				? props.theme.colors.green
				: props.theme.colors.textAccent} !important;
	border: 1px solid
		${props =>
			props.$active
				? 'transparent'
				: props.$completed
					? props.theme.colors.glassBorder
					: 'rgba(255, 255, 255, 0.06)'} !important;
	opacity: 1;
`;

// ─── Sidebar sections ─────────────────────────────────────────────────────────

export const SidebarSection = styled.div`
	margin-bottom: 1.5rem;
	background: rgba(255, 255, 255, 0.02);
	border: 1px solid rgba(52, 211, 153, 0.06);
	border-radius: 12px;
	padding: 1rem;
	transition: border-color 0.2s ease;

	&:hover {
		border-color: rgba(52, 211, 153, 0.12);
	}
`;

export const SectionTitle = styled.h3`
	color: ${props => props.theme.colors.green};
	font-size: 0.8rem;
	font-weight: 600;
	margin-bottom: 0.75rem;
	text-transform: uppercase;
	letter-spacing: 0.8px;
	opacity: 0.9;
`;

export const SectionContent = styled.div`
	color: ${props => props.theme.colors.textAccent};
	font-size: 0.9rem;
	line-height: 1.5;
`;

export const PolygonInfo = styled.div`
	background: rgba(52, 211, 153, 0.03);
	border: 1px solid rgba(52, 211, 153, 0.06);
	border-radius: 10px;
	padding: 0.75rem;
	margin-top: 0.75rem;
	font-size: 0.85rem;

	.info-row {
		display: flex;
		justify-content: space-between;
		margin-bottom: 0.25rem;

		&:last-child {
			margin-bottom: 0;
		}
	}

	.label {
		color: ${props => props.theme.colors.textAccent};
	}

	.value {
		color: ${props => props.theme.colors.textColor};
		font-weight: 500;
	}
`;

// ─── Form controls ────────────────────────────────────────────────────────────

export const ActionButton = styled(Button)`
	&&&& {
		width: 100%;
		margin-top: 1rem;
		border: none;
		border-radius: 10px;
		padding: 0.65rem 1rem;
		font-weight: 600;
		font-size: 0.9rem;
		color: #fff;
		transition: all 0.2s ease;
		background: ${props => {
			if (props.variant === 'danger') return '#dc3545';
			if (props.variant?.startsWith('outline')) return 'rgba(255, 255, 255, 0.08)';
			return props.theme.colors.green;
		}};
		border: ${props =>
			props.variant?.startsWith('outline') ? '1px solid rgba(255, 255, 255, 0.15)' : 'none'};
	}

	&&&&:hover {
		color: #fff;
		transform: translateY(-1px);
		background: ${props => {
			if (props.variant === 'danger') return '#a71d2a';
			if (props.variant?.startsWith('outline')) return 'rgba(255, 255, 255, 0.12)';
			return props.theme.colors.darkGreen;
		}};
	}

	&&&&:disabled {
		color: #fff;
		opacity: 0.4;
		transform: none;
	}
`;

export const FormGroup = styled.div`
	margin-bottom: 1rem;

	label {
		display: block;
		margin-bottom: 0.5rem;
		color: ${props => props.theme.colors.textColor};
		font-size: 0.85rem;
		font-weight: 500;
	}

	input,
	select,
	textarea {
		width: 100%;
		padding: 0.6rem 0.75rem;
		border: 1px solid rgba(52, 211, 153, 0.1);
		border-radius: 8px;
		background: rgba(10, 14, 20, 0.5);
		color: ${props => props.theme.colors.textColor};
		font-family: inherit;
		font-size: 0.9rem;
		transition: all 0.2s ease;
		backdrop-filter: blur(4px);

		&:focus {
			outline: none;
			border-color: ${props => props.theme.colors.green};
			box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.1);
		}

		&::placeholder {
			color: rgba(148, 163, 184, 0.4);
		}
	}

	select {
		background-color: #0e1218;

		option {
			background-color: #0e1218;
			color: ${props => props.theme.colors.textColor};
			padding: 0.5rem;
		}
	}

	input[type='range'] {
		padding: 0;
		background: transparent;
		backdrop-filter: none;
		border: none;

		&:focus {
			box-shadow: none;
		}
	}

	input[type='number'] {
		-moz-appearance: textfield;
		&::-webkit-outer-spin-button,
		&::-webkit-inner-spin-button {
			-webkit-appearance: none;
			margin: 0;
		}
	}

	.range-value {
		display: inline-block;
		min-width: 50px;
		text-align: right;
		color: ${props => props.theme.colors.green};
		font-weight: 600;
	}
`;

export const SettingsGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.75rem;
	margin-top: 1rem;
`;

export const ToggleGroup = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0.6rem 0;
	border-bottom: 1px solid rgba(52, 211, 153, 0.06);

	&:last-child {
		border-bottom: none;
	}

	label {
		margin: 0;
		color: ${props => props.theme.colors.textAccent};
		font-size: 0.85rem;
	}
`;

export const Toggle = styled.button`
	width: 44px;
	height: 24px;
	border-radius: 12px;
	border: none;
	background: ${props =>
		props.$active ? props.theme.colors.green : 'rgba(255, 255, 255, 0.08)'};
	position: relative;
	cursor: pointer;
	transition: all 0.25s ease;
	box-shadow: ${props => (props.$active ? '0 2px 8px rgba(52, 211, 153, 0.3)' : 'none')};

	&::after {
		content: '';
		position: absolute;
		top: 2px;
		left: ${props => (props.$active ? '22px' : '2px')};
		width: 20px;
		height: 20px;
		border-radius: 50%;
		background-color: white;
		transition: left 0.2s ease;
		box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
	}
`;

export const AdvancedToggle = styled.button`
	background: rgba(52, 211, 153, 0.04);
	border: 1px solid rgba(52, 211, 153, 0.1);
	color: ${props => props.theme.colors.textAccent};
	font-size: 0.85rem;
	cursor: pointer;
	padding: 0.6rem 0.75rem;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	width: 100%;
	border-radius: 10px;
	margin-top: 0.5rem;
	transition: all 0.2s ease;
	backdrop-filter: blur(4px);

	&:hover {
		color: ${props => props.theme.colors.green};
		border-color: rgba(52, 211, 153, 0.25);
		background: rgba(52, 211, 153, 0.08);
	}
`;

export const FlightTypeCard = styled.div`
	padding: 1rem;
	border: 1px solid
		${props => (props.$selected ? props.theme.colors.green : 'rgba(52, 211, 153, 0.08)')};
	border-radius: 12px;
	margin-bottom: 0.75rem;
	cursor: pointer;
	transition: all 0.25s ease;
	background: ${props =>
		props.$selected ? 'rgba(52, 211, 153, 0.08)' : 'rgba(255, 255, 255, 0.02)'};
	backdrop-filter: blur(8px);

	&:hover {
		border-color: ${props => props.theme.colors.green};
		background: rgba(52, 211, 153, 0.06);
		transform: translateY(-1px);
		box-shadow: 0 4px 16px rgba(52, 211, 153, 0.08);
	}

	h4 {
		color: ${props => props.theme.colors.textColor};
		font-size: 1rem;
		margin-bottom: 0.25rem;
	}

	p {
		color: ${props => props.theme.colors.textAccent};
		font-size: 0.85rem;
		margin: 0;
	}
`;

// ─── Stats cards ──────────────────────────────────────────────────────────────

export const FlightStatsCard = styled.div`
	background: rgba(52, 211, 153, 0.05);
	border: 1px solid rgba(52, 211, 153, 0.15);
	border-radius: 12px;
	padding: 1rem;
	margin-top: 1rem;
	backdrop-filter: blur(8px);

	h4 {
		color: ${props => props.theme.colors.green};
		font-size: 0.8rem;
		margin: 0 0 0.75rem 0;
		text-transform: uppercase;
		letter-spacing: 0.8px;
		font-weight: 600;
	}
`;

export const StatsGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.5rem;
`;

export const StatItem = styled.div`
	padding: 0.4rem;
	border-radius: 8px;
	background: rgba(255, 255, 255, 0.02);

	.stat-label {
		font-size: 0.7rem;
		color: ${props => props.theme.colors.textAccent};
		text-transform: uppercase;
		letter-spacing: 0.3px;
	}

	.stat-value {
		font-size: 1rem;
		font-weight: 600;
		color: ${props => props.theme.colors.textColor};
	}

	.stat-unit {
		font-size: 0.75rem;
		color: ${props => props.theme.colors.textAccent};
		margin-left: 2px;
	}
`;

export const HighlightStat = styled.div`
	text-align: center;
	padding: 0.75rem;
	background: rgba(52, 211, 153, 0.04);
	border: 1px solid rgba(52, 211, 153, 0.08);
	border-radius: 10px;
	margin-bottom: 0.75rem;

	.stat-label {
		font-size: 0.7rem;
		color: ${props => props.theme.colors.textAccent};
		margin-bottom: 0.25rem;
		text-transform: uppercase;
		letter-spacing: 0.3px;
	}

	.stat-value {
		font-size: 1.5rem;
		font-weight: 700;
		color: ${props => props.theme.colors.green};
	}

	.stat-unit {
		font-size: 0.85rem;
		color: ${props => props.theme.colors.textAccent};
	}
`;

// ─── Grid Reference Search ───────────────────────────────────────────────────

export const GridSearchWrapper = styled.div`
	display: flex;
	gap: 0.5rem;
`;

export const GridSearchInput = styled.input`
	flex: 1;
	min-width: 0;
	padding: 0.5rem 0.75rem;
	border-radius: 8px;
	border: 1px solid rgba(52, 211, 153, 0.2);
	background: rgba(255, 255, 255, 0.03);
	color: ${props => props.theme.colors.textColor};
	font-size: 0.85rem;

	&::placeholder {
		color: #666;
	}

	&:focus {
		outline: none;
		border-color: ${props => props.theme.colors.green};
	}

	&[type='search'] {
		padding-right: 0.75rem;

		&::-webkit-search-cancel-button {
			-webkit-appearance: none;
			appearance: none;
			height: 14px;
			width: 14px;
			background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cline x1='18' y1='6' x2='6' y2='18'/%3E%3Cline x1='6' y1='6' x2='18' y2='18'/%3E%3C/svg%3E")
				center/contain no-repeat;
			cursor: pointer;
		}
	}
`;

export const GridSearchButton = styled.button`
	padding: 0.5rem 0.75rem;
	border-radius: 8px;
	border: 1px solid rgba(52, 211, 153, 0.3);
	background: rgba(52, 211, 153, 0.1);
	color: ${props => props.theme.colors.green};
	cursor: pointer;
	display: flex;
	align-items: center;
	flex-shrink: 0;

	&:hover {
		background: rgba(52, 211, 153, 0.2);
	}
`;

export const GridSearchError = styled.small`
	color: #f87171;
	font-size: 0.8rem;
	display: block;
	margin-top: 0.25rem;
`;

export const WarningHint = styled.small.attrs({ 'aria-live': 'polite' })`
	color: #ffc107;
	font-size: 0.75rem;
	display: block;
	margin-top: 0.25rem;
`;
